import * as React from 'react';
import * as i18n from 'i18next';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { PortalCheckboxField } from 'components/FormControls';
import {
	formatMinServiceDuration,
	formatMinServicePrice,
	generateId,
	generateKey,
	getAllBookableChildren,
	getServicesDeep,
	isBookable,
} from 'helpers/bookingTemplates';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { Expandable } from 'components/Common/Expandable';

interface IListProps {
	groupedServices: IServicesList[][];
	filteredGroupedServices?: IServicesList[][]; // for subservices on shop page
	serviceGroups: IServiceGroup[];
	selected: Array<IServicesList['variantParentId']>;
	toggle: (variantParentId: IServicesList['variantParentId']) => void;
	t?: (key: string, options?: any) => string;
	currencyKey?: number;
}

interface IItemProps extends IListProps {
	item: IServiceGroup | IServicesList[];
	level: number;
}

const summary = (
	title: string,
	services: IServicesList[],
	currencyKey?: number,
	t?: (key: string, options?: any) => string,
): JSX.Element => (
	<div className="text">
		<h3 className="serviceCard__title">
			{title}
			{hasDiscountsOrIsBundle(services) ? (
				<span className="serviceCard__discount">{t('BSPortal_Common_Sale')}</span>
			) : null}
		</h3>
		<p className="muted description">{services[0].description}</p>
		<p className="priceDuration">
			{[formatMinServicePrice(services, i18n.t, currencyKey), formatMinServiceDuration(services, i18n.t)]
				.filter(Boolean)
				.join(' • ')}
		</p>
	</div>
);

const hasDiscountsOrIsBundle = (services: IServicesList[]) => {
	return services.some((service) => {
		if (Math.floor(service.originalPrice * 100) !== Math.floor(service.price * 100)) {
			return true;
		}
		const customData = JSON.parse(service.customData || '{}');
		if (customData && 'priceSum' in customData) {
			return Math.floor(customData.priceSum * 100) != Math.floor(service.price * 100);
		} else {
			// Old bundles do not have priceSum in custom data
			return customData.isBundle;
		}
	});
};

const ServiceGroupOrService: React.FC<IItemProps> = (props: IItemProps) => {
	const { level, item, serviceGroups, groupedServices, filteredGroupedServices, selected, toggle, currencyKey, t } =
		props;

	let service: IServicesList[];
	let title: string = '';
	let bookableChildren: ReturnType<typeof getAllBookableChildren> = [];
	const className = 'serviceCard card dark d-flex flex-row ';

	// show as final service if serviceGroup has only 1 variation
	if (Array.isArray(item)) {
		service = item;
		title = item[0]?.name || '';
	} else {
		// would be better to use filteredGroupedServices but it causes subservices to render incorrectly because of levels
		bookableChildren = getAllBookableChildren(item.id, serviceGroups, groupedServices);
		if (bookableChildren.length === 1 && Array.isArray(bookableChildren[0])) {
			service = bookableChildren[0];
			title = item.value;
		}
	}
	if (service) {
		// If subservice contains only 1 variation (when filtering by members)
		if (level === 2) {
			const flatFilteredGroupServices = filteredGroupedServices.reduce((acc, val) => acc.concat(val), []) || [];
			if (flatFilteredGroupServices?.length) {
				service = service.filter((s) => flatFilteredGroupServices.includes(s));
			}
		}
		if (!service.length) {
			return null;
		}
		const first = service[0];
		const active = selected.includes(first.variantParentId);
		return (
			<label className={className + (active ? 'active' : '')}>
				<div
					className="checkbox"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<PortalCheckboxField
						input={{
							type: 'checkbox',
							name: 'chosenService',
							checked: active,
						}}
						inLabelAlready={true}
						type="checkbox"
						onChangeAction={() => {
							toggle(first.variantParentId);
						}}
					/>
				</div>
				{summary(title, service, currencyKey, t)}
			</label>
		);
	}
	if (Array.isArray(item)) {
		console.error('This should be unreachable');
		return null;
	}
	if (!bookableChildren.length) {
		return null;
	}
	const childrenRendered = bookableChildren.map((ser) => (
		<ServiceGroupOrService key={generateKey(ser)} {...props} item={ser} level={level + 1} />
	));
	if (level === 0) {
		return (
			<div id={generateId(item)}>
				<h2 className="subtitle">{item.value}</h2>
				{childrenRendered}
			</div>
		);
	} else {
		const gservices = getServicesDeep(item.id, serviceGroups, groupedServices);
		const flatServices = gservices.reduce((acc, val) => acc.concat(val), []);
		if (!flatServices.length) {
			return null;
		}
		return (
			<Expandable closed={true} className={className} title={summary(item.value, flatServices, currencyKey, t)}>
				<div className="subServices">{childrenRendered}</div>
			</Expandable>
		);
	}
};

const SelectableServicesList: React.FC<IListProps> = (props: IListProps) => {
	const { serviceGroups, groupedServices } = props;

	return (
		<div className="selectableServicesList">
			{serviceGroups
				.filter((sg) => !sg.parentId && isBookable(sg.id, serviceGroups, groupedServices))
				.map((sg) => (
					<ServiceGroupOrService key={sg.id} {...props} item={sg} level={0} />
				))}
		</div>
	);
};

export { SelectableServicesList };
